import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';

import { AuthError, RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';

import { Observable, from, of, BehaviorSubject } from 'rxjs';
import { map, tap, switchMap, filter, take, catchError } from 'rxjs/operators';

import { SupabaseService } from './supabase.service';
import { UserRole } from '../core/types/user.types';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  last_name?: string;
  first_name?: string;
  patronymic?: string;
  role: UserRole;
  department_id?: string;
  separated_enterprise_id?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  isAuthenticated = computed(() => !!this.authUserSignal()?.email);
  initialized$: Observable<boolean>;

  private supabase: SupabaseClient;
  private authUserSignal = signal<{ id: string; email: string } | null>(null);
  private userProfileSignal = signal<UserProfile | null>(null);
  private isLoadingSignal = signal<boolean>(false);
  private initializedSubject = new BehaviorSubject<boolean>(false);
  private supabaseService = inject(SupabaseService);
  private realtimeChannel: RealtimeChannel | null = null;

  // Public readonly signals
  profile = this.userProfileSignal.asReadonly();

  constructor(
    private router: Router
  ) {
    this.supabase = this.supabaseService.client;
    this.initialized$ = this.initializedSubject.asObservable();
    this.initializeApp();
  }

  async initializeApp(): Promise<boolean> {
    try {
      await this.loadSession();
      return true;
    } catch (error) {
      console.error('Failed to initialize auth state', error);
      return false;
    } finally {
      this.initializedSubject.next(true);
    }
  }

  isAuthenticated$(): Observable<boolean> {
    return this.initialized$.pipe(
      filter(initialized => initialized),
      take(1),
      map(() => this.isAuthenticated())
    );
  }

  login(email: string, password: string): Observable<any> {
    this.isLoadingSignal.set(true);

    return from(
      this.supabase.auth.signInWithPassword({
        email,
        password
      })
    ).pipe(
      switchMap(async response => {
        if (response.error) {
          this.isLoadingSignal.set(false);
          return {error: response.error};
        }

        if (response.data.user) {
          this.authUserSignal.set({
            id: response.data.user.id,
            email: response.data.user.email || ''
          });
          
          // Load user profile from database
          await this.loadUserProfile(response.data.user.id);
          this.isLoadingSignal.set(false);
        }

        this.isLoadingSignal.set(false);
        return response;
      })
    );
  }

  clearStateAndRedirectToLogin(): void {
    this.clearAuthState();
    this.router.navigate(['/sign-in']);
  }

  logout(): Observable<any> {
    return from(this.supabase.auth.signOut({
      scope: 'local'
    })).pipe(
      catchError(error => {
        this.clearStateAndRedirectToLogin()
        return of();
      }),
      tap(() => {
        this.clearStateAndRedirectToLogin();
      })
    );
  }

  getCurrentUser() {
    const authUser = this.authUserSignal();

    if (!authUser) return null;

    return {
      id: authUser.id,
      email: authUser.email
    };
  }

  getAuthUserEmail(): string {
    return this.authUserSignal()?.email || '';
  }

  resetPassword(email: string): Promise<{ data: {}; error: null } | { data: null; error: AuthError }> {
    return this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
  }

  updatePassword(new_password: string) {
    return from(this.supabase.auth.updateUser({
      password: new_password,
    }));
  }

  private async loadUserProfile(userId: string): Promise<void> {
    try {
      const { data, error } = await this.supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error loading user profile:', error);
        return;
      }
      
      this.userProfileSignal.set(data as UserProfile);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }

  hasRole(role: UserProfile['role']): boolean {
    return this.userProfileSignal()?.role === role;
  }

  hasAnyRole(roles: UserProfile['role'][]): boolean {
    const currentRole = this.userProfileSignal()?.role;
    return currentRole ? roles.includes(currentRole) : false;
  }

  private clearAuthState(): void {
    this.authUserSignal.set(null);
    this.userProfileSignal.set(null);
    this.unsubscribeFromRealtime();
    this.supabase.removeAllChannels();
  }

  private unsubscribeFromRealtime(): void {
    if (this.realtimeChannel) {
      this.supabase.removeChannel(this.realtimeChannel);
      this.realtimeChannel = null;
    }
  }

  private async loadSession(): Promise<void> {
    try {
      const {data} = await this.supabase.auth.getSession();

      if (data.session?.user) {
        this.authUserSignal.set({
          id: data.session.user.id,
          email: data.session.user.email || ''
        });
        
        // Load user profile
        await this.loadUserProfile(data.session.user.id);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      this.clearAuthState();
    }
  }
}
